// public/v3/src/lib/eligibility-view.js
// Pure shaping for the eligibility summary chip + expander. CommonJS.
// Moves the row/status helpers out of eligibility-panel.jsx and adds a model
// that keeps raw error codes OFF the surface (behind the expander).
'use strict';

const {
  eligibilityInsuranceId,
  inferQmbStatus,
  isMedicaidInsurance,
  isMedicareInsurance,
  latestCheckForInsurance,
  qmbCheckedAt,
} = require('./eligibility-infer');

/**
 * Maps a raw eligibility status string to a tone class token.
 * Mirrors the inline statusClass() in eligibility-panel.jsx exactly.
 */
function statusClass(status) {
  if (status === 'active') return 'ok';
  if (status === 'inactive') return 'warn';
  if (status === 'error') return 'bad';
  return 'muted';
}

/**
 * Returns the Medicare/Medicaid insurance rows with their latest check.
 * Mirrors relevantRows() in eligibility-panel.jsx exactly.
 */
function relevantRows(patient) {
  const raw = (patient && patient._raw) || {};
  const checks = Array.isArray(raw.eligibilityChecks) ? raw.eligibilityChecks : [];
  const insurances = Array.isArray(raw.insurances) ? raw.insurances : [];
  return insurances
    .filter(i => eligibilityInsuranceId(i) && (isMedicareInsurance(i) || isMedicaidInsurance(i)))
    .map(insurance => ({
      insurance,
      latest: latestCheckForInsurance(checks, eligibilityInsuranceId(insurance)),
    }));
}

/**
 * Returns a human-readable detail string for the latest check row.
 * Mirrors latestDetail() in eligibility-panel.jsx exactly.
 * NOTE: when status === 'error', error codes are returned joined here for
 * internal use; the model surfaces them as a separate errorCodes array.
 */
function rowDetail(latest) {
  if (!latest) return 'no cached check';
  if (latest.insuranceType) return latest.insuranceType;
  if (latest.qmbSignal) return 'QMB text in full report';
  if (Array.isArray(latest.errors) && latest.errors.length) return latest.errors.join(', ');
  return 'type not reported';
}

/**
 * Returns the display label for the primary insurance.
 * Mirrors the JSX inline in eligibility-panel.jsx exactly.
 */
function payerLabel(insurance) {
  if (!insurance) return 'Eligibility';
  return insurance.carrier || insurance.paymentProgram || insurance.rank || 'Insurance';
}

/**
 * Shapes all the data the eligibility chip + expander needs.
 *
 * Returns { available: false } when patient has no _raw (chip renders nothing).
 *
 * Key fields:
 *   available     — boolean; false = no _raw, chip should not render
 *   hasRows       — boolean; true if any Medicare/Medicaid insurance found
 *   payer         — string; display name for the primary insurance
 *   status        — raw status string ('active'|'inactive'|'error'|'no check'|'none')
 *   statusLabel   — human label ('check failed' for 'error', otherwise = status)
 *   statusClass   — tone class token ('ok'|'warn'|'bad'|'muted')
 *   errorCodes    — array of raw error code strings (empty when no errors)
 *                   rendered BEHIND the expander, NOT on the chip surface
 *   checkedAt     — ISO string from qmb.checkedAt or '' if not present
 *   conclusion    — QMB inference conclusion string
 *   rows          — [{insurance, latest}] for the expander detail list
 */
function eligibilitySummaryModel(patient) {
  if (!patient || !patient._raw) return { available: false };

  const raw = patient._raw;
  const rows = relevantRows(patient);
  const inference = inferQmbStatus(patient);
  const checkedAt = qmbCheckedAt(raw.qmb) || null;
  const conclusion = inference.confirmed
    ? `QMB confirmed (${inference.source})`
    : inference.dualSignal
      ? 'Medicare/Medicaid active; QMB needs confirmation'
      : 'No QMB signal';

  const primary = rows[0] || null;
  const status = primary
    ? (primary.latest ? primary.latest.status : 'no check')
    : 'none';

  const errorCodes = (primary && primary.latest && Array.isArray(primary.latest.errors))
    ? primary.latest.errors.slice()   // defensive copy — keep raw array off the surface
    : [];

  return {
    available: true,
    hasRows: rows.length > 0,
    payer: payerLabel(primary && primary.insurance),
    status,
    statusLabel: status === 'error' ? 'check failed' : status,
    statusClass: statusClass(status),
    errorCodes,
    checkedAt,
    conclusion,
    rows,
  };
}

module.exports = {
  statusClass,
  relevantRows,
  rowDetail,
  payerLabel,
  eligibilitySummaryModel,
};
